const post = {
  slug: 'what-is-a-nominee',
  title: 'What is a nominee? Why every asset you own needs one',
  excerpt:
    'A nominee is the person who can claim your bank account, PPF, EPF, insurance, or investments when you are no longer here. Here is what that actually means, why institutions keep asking you to add one, and why it still fails if your family does not know.',
  date: '2026-08-27',
  updated: '2026-08-27',
  category: 'Guides',
  featured: true,
  coverImage: '/blog/what-is-a-nominee/cover.jpg',
  coverAlt: 'Family records on a dining table: passbook, insurance folder, and house keys.',
  keywords:
    'what is a nominee, nominee meaning India, why add nominee, bank nominee, PPF nominee, EPF nominee, insurance nominee, legal heir vs nominee',
  seoTitle: 'What is a Nominee? Why Every Asset Needs One',
  seoDescription:
    'A clear guide to nominees in India: what a nominee is, how they differ from legal heirs, why banks and funds insist you add one, and why your family still needs to know.',
  related: ['why-nominees-still-need-to-be-told', 'how-to-add-a-nominee-in-india'],
  faqs: [
    {
      q: 'Is a nominee the same as a legal heir?',
      a: 'No. A nominee is the person named on an account or policy to claim it after your death. Legal heirs are the people entitled to inherit under a will or succession law. In many products the nominee collects first; the estate may still need to be settled among heirs. Rules differ by bank deposits, insurance, shares, and provident funds.',
    },
    {
      q: 'If I already made a will, do I still need a nominee?',
      a: 'Yes. A will says who should inherit. A nominee tells the bank, EPFO, insurer, or registrar who may claim the asset without hunting through court papers first. They work together. Skipping nomination often means frozen accounts even when a will exists.',
    },
    {
      q: 'Can I name more than one nominee?',
      a: 'Often yes, with share percentages—PPF, many bank deposits, and mutual fund folios allow this. Some products allow only one. Check each account. If you have several assets, keep the names consistent unless you have a reason not to.',
    },
    {
      q: 'What happens if I never add a nominee?',
      a: 'The institution usually will not release money to a relative on a phone call. Your family may need a succession certificate, legal heir certificate, or a long claim process. That delay is a major reason deposits, EPF, and insurance stay unclaimed for years.',
    },
    {
      q: 'Does adding a nominee on KeepMyAsset replace the bank form?',
      a: 'No. The legal nomination lives with the bank, EPFO, insurer, or registrar. KeepMyAsset records which assets exist, who you named, and helps inform those people when a verified family request is approved. It does not replace Form DA1, Form 2, or an insurance nomination.',
    },
  ],
  body: `
Almost every Indian form you fill—bank account, PPF, EPF, LIC, demat, mutual fund—asks for a nominee. Most people write a name because the clerk circled the box. Few people can explain what that name actually does.

That gap matters. Nomination is one of the simplest protections you can give your family. It is also one of the most misunderstood. A nominee is not a magic transfer of ownership. It is not a substitute for a will. And a name on a form does not help if nobody in the family knows the account exists.

:::stat ₹82,000 Cr+
sits unclaimed in India—often because families never knew the asset, or never knew they were named on it.
:::

## What is a nominee?

A nominee is the person you name on an asset so that, after your death, someone the institution already recognises can step forward and claim it.

Think of it as an instruction to the institution, not a private note to your family. The bank, the EPFO, the insurer, or the depository does not know your relatives. They know the name on their records. When that name is missing, they freeze the asset until a legal process proves who may claim it.

![A passbook and a nomination card: the institution follows the name on file, not a WhatsApp message.](/blog/what-is-a-nominee/passbook.jpg)

:::callout In one sentence
A nominee is the person authorised to claim an account, policy, or investment after you are gone—so your family does not have to start from zero with a locked passbook and no paperwork.
:::

You typically add a nominee when you open the account, buy the policy, or later by submitting a nomination form. You can usually change that name while you are alive. After death, the institution follows the nomination on file—not a WhatsApp message, not a verbal promise.

## Nominee, legal heir, and will—three different jobs

This is where most confusion starts. People treat “nominee” as “the person who inherits.” That is not always true, and the difference depends on the product.

- A nominee is the person the institution may pay or transfer to first. For many bank deposits, courts have treated the nominee as a trustee who collects for the legal heirs. For some insurance policies, a spouse, parent, or child named as nominee can be a beneficial nominee under the Insurance Act.
- Legal heirs are the people entitled to the estate under a will or, if there is no will, under succession law.
- A will records your wishes for the estate as a whole. It does not automatically unfreeze a PPF account or an EPF balance the week after a death.

You need both layers. Nomination gets the claim started with the institution. A will (and clear records) settles who should ultimately benefit. Skipping nomination because “the will covers it” is how families spend months on affidavits for an FD nobody disputed.

## Why a nominee is actually helpful

Nomination is not paperwork for the bank’s sake. It is a kindness to the people who will be grieving, travelling, and hunting for documents at the same time.

### Claims move faster

With a nomination on file, the institution has a named person, a relationship, and usually ID details. Without one, families are asked for a succession certificate, legal heir certificate, indemnity, or a court process. That can take months. Meanwhile school fees, EMIs, and medical bills do not wait.

### Fewer fights inside the family

When nobody is named, every relative can feel entitled to “help” with the account. When someone is named, the first conversation with the bank is clearer. Nomination does not erase every dispute, but it removes the worst kind: the institution that will speak to no one.

### Money is less likely to vanish into unclaimed pools

Unclaimed deposits, forgotten EPF, lapsed insurance, and shares transferred to the IEPF are not abstract. They happen when the asset is real and the family never finds it—or finds it too late. A nominee does not guarantee discovery. It does guarantee that, once the account is found, there is a path to claim it.

### You keep control while you are alive

A nominee has no right to operate your account during your lifetime. Adding your spouse, child, or parent does not give them a debit card. You can change the nomination whenever your life changes—marriage, divorce, a new child, a parent’s death.

## Why banks, funds, and insurers keep asking you to add one

If it feels like every form now insists on a nominee, that is not a fad. Institutions got tired of holding money they could not legally release.

- Banks take nomination under deposit rules so a survivor can claim without a court file for every savings account and FD.
- EPFO and PPF need a named person because provident balances are often a family’s largest financial asset after the house.
- Insurers need a nominee so a claim is not stuck while heirs argue or while papers are still being collected.
- SEBI has pushed nomination for demat accounts and mutual fund folios so investments do not sit ownerless when the holder dies.

Regulators are not being sentimental. Unclaimed money is an operational and political problem. The easiest prevention is a name, a share percentage, and a form on file before anything goes wrong.

That is why the clerk will not let you skip the field. That is why apps now nag you until the nomination is complete. They have seen what happens when it is blank.

## What happens if you never add a nominee

The account does not become public. It becomes stuck.

1. The bank or office marks the holder as deceased once they are told—or the account simply goes dormant if nobody tells them.
2. Relatives cannot withdraw, close, or transfer on goodwill. Staff are not allowed to “just help the family.”
3. Someone must prove legal right: nomination if it exists, otherwise heirship documents, a will with probate where required, or a court order.
4. If nobody claims in time, balances can move into unclaimed deposits, IEPF, or insurer unclaimed funds. Getting them back is a second, slower project.

This is not rare. It is the default outcome for people who were organised in every other way—except they treated nomination as optional fine print.

## Myths that stop people from adding a nominee

- “The nominee will take everything away from the rest of the family.” In many deposit cases the nominee collects; heirs may still have rights. For insurance, check whether the nominee is a beneficial nominee. If you want a specific split, write a will and keep nominations aligned with it.
- “My spouse is joint holder, so I do not need a nominee.” Joint holding and nomination are different. Joint operation can fail if the surviving holder is not set up the way you think. Nomination still belongs on the account.
- “I am young. This can wait.” The form takes minutes. The claim process without it does not. Accidents do not check age.
- “I told my son about the locker verbally.” Institutions follow records. Verbal instructions are not a nomination.
- “I put the same person everywhere, so I am done.” You still need the form on each product—bank, PPF, EPF, LIC, demat, mutual funds, housing society. One name in your head is not one name on every ledger.

## The part nomination does not solve

Here is the uncomfortable truth: a perfect nomination on a PPF account is useless if your family does not know the PPF exists.

Banks will not email your children a list of your FDs. EPFO will not call your spouse. The insurer will not knock on the door because a premium stopped. Nomination tells the institution who may claim. It does not tell the nominee that they were named, or where to look.

> Nomination answers “who can claim this asset?” It does not answer “does anyone know this asset exists?” Both questions have to be true, or the money still sits.

That second question is why families lose years—and why KeepMyAsset exists. Record the asset, record who you named, and give your family a way to be guided when a verified request is approved. The legal nomination still lives with the bank or the EPFO. The map of your life should not live only in your head.

## What to do this week

1. List every asset: savings and FDs, PPF, EPF, NPS, insurance, demat, mutual funds, lockers, property papers, loans you want the family to know about.
2. Open each one and confirm a nominee is on file—not “I think I filled it in 2014.” Download or photograph the acknowledgement.
3. Use the same trusted people unless you have a reason to split. Add a contingent nominee if the product allows it.
4. Tell at least one adult in the family that nominations exist, without handing over passwords or OTPs.
5. Keep a secure record of what you own and who you named, so the people you love are not starting from a blank dining table.

Nomination is not a morbid task. It is how you make sure the people you already trust can actually reach what you built for them.

This article is educational, not legal advice. Nomination and succession rules differ by product and by personal situation. If the estate is large or disputed, speak with a qualified advisor.
`,
};

export default post;
